'use client';

import {Tabs as TabsPrimitive} from '@base-ui/react/tabs';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../../lib/utils';

const tabsListVariants = cva('flex items-stretch border-b border-border-subtle', {
  variants: {
    variant: {
      secondary: 'gap-3',
      primary: 'gap-1'
    }
  },
  defaultVariants: {variant: 'primary'}
});

const tabsTriggerVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap',
    'cursor-pointer transition-colors',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
    'data-disabled:text-fg-disabled data-disabled:pointer-events-none'
  ],
  {
    variants: {
      variant: {
        secondary: [
          'px-1 pb-3 -mb-px border-b-2 border-transparent',
          'text-fg-tertiary hover:text-fg',
          'data-active:border-brand-accent data-active:text-fg'
        ],
        primary: [
          'px-4 py-[10.25px] rounded-t-lg',
          'text-fg-tertiary hover:text-fg',
          'data-active:bg-background-surface data-active:border data-active:border-border',
          'data-active:border-b-background-surface data-active:-mb-px',
          'data-active:text-fg'
        ]
      }
    },
    defaultVariants: {variant: 'primary'}
  }
);

type TabsVariant = VariantProps<typeof tabsListVariants>['variant'];

function Tabs({className, ...props}: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root data-slot="tabs" className={cn('flex flex-col', className)} {...props} />
  );
}

interface TabsListProps extends TabsPrimitive.List.Props {
  variant?: TabsVariant;
  hideBorder?: boolean;
}

function TabsList({className, variant, hideBorder = false, ...props}: TabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListVariants({variant}), hideBorder && 'border-b-0', className)}
      {...props}
    />
  );
}

interface TabsTriggerProps extends TabsPrimitive.Tab.Props {
  variant?: TabsVariant;
}

function TabsTrigger({className, variant, ...props}: TabsTriggerProps) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({variant}), className)}
      {...props}
    />
  );
}

function TabsContent({className, ...props}: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel data-slot="tabs-content" className={cn('mt-4', className)} {...props} />
  );
}

export {Tabs, TabsList, TabsTrigger, TabsContent};
export type {TabsVariant};
